// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, Component, Children, cloneElement, isValidElement } from '../../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import Menu, { MenuItem, MenuHeader, MenuBaseItem, MenuDivider } from '../Menu';
import Dropdown from '../Dropdown';
import Button from '../Button';

/**
 * `ToolbarGroup` is a part of the [Toolbar](#!/Toolbar) component similar to [ButtonGroup](#buttongroup).
 * It is used for grouping several toolbar controls together.
 */
class ToolbarGroup extends Component {
    isResponsive() {
        return this.context.responsive && Children.count(this.props.children) > 1;
    }

    convertToMenu(child, isLast) {
        if (child.type === Button) {
            return (
                <MenuItem
                    disabled={child.props.disabled}
                    icon={child.props.icon}
                    // eslint-disable-next-line react/jsx-handler-names
                    onClick={child.props.onClick}
                >
                    {child.props.children}
                </MenuItem>
            );
        }

        if (child.type === Dropdown) {
            return [
                child.props.children ? <MenuHeader>{child.props.children}</MenuHeader> : null,
                child.props.menu.props.children,
                isLast ? null : <MenuDivider />,
            ];
        }

        return (
            <MenuBaseItem>{child}</MenuBaseItem>
        );
    }

    render() {
        const { baseClassName, className, children, title, ...props } = this.props;

        const childrenCount = Children.count(children);
        const isCollapsed = this.isResponsive();

        let childElement = Children.map(children, (child, index) => {
            if (!isValidElement(child)) {
                return child;
            }

            if (isCollapsed) {
                return this.convertToMenu(child, index === childrenCount - 1);
            }

            return cloneElement(child, {
                className: classNames(
                    `${baseClassName}-item`,
                    child.props.className
                ),
            });
        });

        if (isCollapsed) {
            childElement = <Dropdown menu={<Menu>{childElement}</Menu>}>{title}</Dropdown>;
        }

        return (
            <div className={classNames(baseClassName, className)} {...props}>
                {childElement}
            </div>
        );
    }
}

ToolbarGroup.propTypes = {
    /**
     * Title.
     */
    title: PropTypes.node.isRequired,
    /**
     * Content of the `ToolbarGroup`.
     */
    children: PropTypes.node,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};

ToolbarGroup.defaultProps = {
    children: null,
    className: null,
    baseClassName: `${CLS_PREFIX}toolbar__group`,
};

ToolbarGroup.contextTypes = {
    responsive: PropTypes.bool,
};

export default ToolbarGroup;



// WEBPACK FOOTER //
// ./components/Toolbar/ToolbarGroup.js